import { create } from 'zustand';

const useAuthStore = create((set, get) => ({
      // State
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,

      // Actions
      setAuth: (user, token) => {
        set({
          user,
          token,
          isAuthenticated: true,
          error: null,
        });
        // Also store in localStorage for compatibility
        localStorage.setItem('jwt', token);
      },

      clearAuth: () => {
        set({
          user: null,
          token: null,
          isAuthenticated: false,
          error: null,
        });
        // Clear from localStorage
        localStorage.removeItem('jwt');
        localStorage.removeItem('adminToken');
        localStorage.removeItem('adminUser');
      },

      setLoading: (loading) => set({ isLoading: loading }),
      
      setError: (error) => set({ error }),

      // Check if user has admin role
      isAdmin: () => {
        const { user } = get();
        // Check both direct role property and roles array
        if (user?.role === 'owner' || user?.role === 'admin') {
          return true;
        }
        // Check roles array if available
        if (user?.roles && Array.isArray(user.roles)) {
          return user.roles.some(role => role.role === 'owner' || role.role === 'admin');
        }
        return false;
      },

      // Initialize auth from localStorage (for compatibility)
      initializeAuth: async () => {
        set({ isLoading: true });
        
        try {
          // Check for existing JWT token
          const token = localStorage.getItem('jwt') || localStorage.getItem('adminToken');
          
          if (!token) {
            set({ isLoading: false });
            return false;
          }

          // Validate token with API
          const response = await fetch('/api/auth/me', {
            headers: {
              'Authorization': `Bearer ${token}`,
            },
          });

          if (response.ok) {
            const userData = await response.json();
            // Merge roles into user object for easier access
            const user = userData.user || userData;
            if (userData.roles && Array.isArray(userData.roles)) {
              user.roles = userData.roles;
              // Also set primary role for backward compatibility
              if (userData.roles.length > 0) {
                user.role = userData.roles[0].role;
              }
            }
            set({
              user,
              token,
              isAuthenticated: true,
              isLoading: false,
              error: null,
            });
            return true;
          } else {
            // Token is invalid, clear auth
            get().clearAuth();
            set({ isLoading: false });
            return false;
          }
        } catch (error) {
          console.error('Auth initialization error:', error);
          get().clearAuth();
          set({ isLoading: false, error: error.message });
          return false;
        }
      },

      // Login function
      login: async (email, password, isAdmin = false) => {
        set({ isLoading: true, error: null });

        try {
          const endpoint = isAdmin ? '/api/admin/login-enhanced' : '/api/auth/login';
          
          const response = await fetch(endpoint, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ email, password }),
          });

          const data = await response.json();

          if (response.ok) {
            // Handle different response structures
            let user = data.user;
            let token = data.token || data.access_token; // Support both token formats
            
            if (isAdmin && user) {
              // For admin login, the role is already in the user object
              // No additional processing needed
            }
            
            // Store both access and refresh tokens for SaaS users
            if (data.access_token) {
              localStorage.setItem('jwt', data.access_token); // Use consistent key
              localStorage.setItem('userToken', data.access_token); // Keep for compatibility
              localStorage.setItem('refreshToken', data.refresh_token);
              localStorage.setItem('userInfo', JSON.stringify(user));
            }
            
            get().setAuth(user, token);
            set({ isLoading: false });
            return { success: true, user: user };
          } else {
            set({ isLoading: false, error: data.detail || data.message || 'Login failed' });
            return { success: false, error: data.detail || data.message || 'Login failed' };
          }
        } catch (error) {
          set({ isLoading: false, error: error.message });
          return { success: false, error: error.message };
        }
      },

      // Registration function
      register: async (userData) => {
        set({ isLoading: true, error: null });

        try {
          const response = await fetch('/api/auth/register', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(userData),
          });

          const data = await response.json();

          if (response.ok) {
            set({ isLoading: false });
            return { success: true, message: data.message };
          } else {
            set({ isLoading: false, error: data.message || 'Registration failed' });
            return { success: false, error: data.message || 'Registration failed' };
          }
        } catch (error) {
          set({ isLoading: false, error: error.message });
          return { success: false, error: error.message };
        }
      },

      // Logout function
      logout: () => {
        get().clearAuth();
      },
    }));

export default useAuthStore;
